import { State, process } from '@progress/kendo-data-query';
import { formatNumber } from '@progress/kendo-intl';
import { GridDataStateChangeEvent, GridPagerSettings } from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTransactionColumns } from 'src/assets/static/view_checkbook/useTransactionColumns';
import GridWrapper from 'src/components/GridWrapper';
import useGLPeriodTransactionApi, {
  GLPeriodTransactionResponse,
} from 'src/services/useGLPeriodTransactionApi';
import exportToExcel from 'src/utils/exportToExcel';
import CheckbookBalanceToolBar from './CheckbookBalanceToolBar';
const DATA_ITEM_KEY: string = 'rowId';
const initialDataState: State = {
  skip: 0,
  take: 20,
  sort: [
    {
      field: 'date',
      dir: 'desc',
    },
  ],
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};
const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};
const defaultProps = {
  tableTextId: 'GL Code Assignments',
  DataItemKey: DATA_ITEM_KEY,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

interface Props {
  glCodeId?: number;
  glPeriodId?: number;
  isOpenTransaction?: boolean;
}

const TransactionTable: FC<Props> = ({ glCodeId, glPeriodId, isOpenTransaction }) => {
  const { t } = useTranslation();
  const { glPeriodTransactionLog, getGLPeriodTransactionLog, isSpentLoading } =
    useGLPeriodTransactionApi();

  const transactionColumns = useTransactionColumns(glCodeId ? false : true);

  const [dataState, setDataState] = useState(initialDataState);
  const [exportLoading, setExportLoading] = useState(false);

  const { data, total } = useMemo(
    () =>
      process(glPeriodTransactionLog, dataState) as {
        data: GLPeriodTransactionResponse[];
        total: number;
      },
    [dataState, glPeriodTransactionLog]
  );

  const onDataStateChange = useCallback((e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  }, []);

  const handleClearFilter = useCallback(() => {
    setDataState((prev) => ({
      ...initialDataState,
      skip: prev.skip,
      take: prev.take,
    }));
  }, []);

  const calculateExcelData = useCallback(
    (page: number) => {
      let excelData: any[] = [];
      let rows: GLPeriodTransactionResponse[] = [];
      if (page === 1) {
        rows = data;
      } else if (page === 2) {
        const { data: filteredData } = process(glPeriodTransactionLog, {
          filter: dataState.filter,
          sort: dataState.sort,
        }) as {
          data: GLPeriodTransactionResponse[];
          total: number;
        };
        rows = filteredData;
      }
      rows.forEach((obj: any) => {
        const newData = {
          ...obj,
          total: formatNumber(obj.total, 'c2'),
          balance: formatNumber(obj.balance, 'c2'),
          forecast: formatNumber(obj.forecast, 'c2'),
        };
        excelData.push(newData);
      });
      return excelData;
    },
    [data, dataState.filter, dataState.sort, glPeriodTransactionLog]
  );

  const excelExport = useCallback(
    (page: number) => {
      const rows = calculateExcelData(page);
      const columns: string[] = [];
      const columnMapping: Record<string, string> = {};
      const excelColumns = [...transactionColumns];
      excelColumns.forEach((item) => {
        if (item.field === 'date') {
          columns.push('transactionDate');
          Object.assign(columnMapping, { transactionDate: item.title });
        } else {
          columns.push(item.field as string);
          Object.assign(columnMapping, { [item.field as string]: item.title });
        }
      });
      exportToExcel({
        columns,
        rows,
        fileName: t('Checkbook Transactions'),
        columnMapping,
        sheetName: t('checkbook'),
      });
    },
    [calculateExcelData, t, transactionColumns]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <CheckbookBalanceToolBar
        setExportLoading={setExportLoading}
        clearFilter={handleClearFilter}
        excelExport={excelExport}
        dataState={dataState}
        isOpenTransaction={isOpenTransaction}
      />
    ),
    [dataState, excelExport, handleClearFilter, isOpenTransaction]
  );

  useEffect(() => {
    if (glCodeId && glPeriodId) {
      getGLPeriodTransactionLog({ glCodeId, glPeriodId: glPeriodId });
    } else {
      getGLPeriodTransactionLog({ glPeriodId: glPeriodId || 0 });
    }
  }, [getGLPeriodTransactionLog, glCodeId, glPeriodId]);
  return (
    <GridWrapper
      id="TransactionGrid"
      columns={transactionColumns}
      dataSource={data}
      dataState={dataState}
      height={'60vh'}
      total={total}
      loading={isSpentLoading || exportLoading}
      onDataStateChange={onDataStateChange}
      gridToolbarChildren={gridToolbarChildren}
      {...defaultProps}
    />
  );
};

export default TransactionTable;
